import { useState } from "react";
import { Bell, Sparkles } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { RoleSwitcher } from "./RoleSwitcher";
import { NextStepHint } from "./NextStepHint";
import { ModeToggle } from "./ModeToggle";
import { UtilitiesMenu } from "./UtilitiesMenu";
import { IaContextDrawer } from "@/components/IaContextDrawer";
import { useDemoStore } from "@/store/useDemoStore";

interface AppLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  breadcrumb?: string[];
}

const avatarByRole: Record<string, string> = {
  admin: "AA",
  alumno: "JR",
  evaluador: "MT",
  dictaminador: "EV",
  instructor: "RF",
  auditor: "VA",
};

export function AppLayout({ children, pageTitle = "Dashboard", breadcrumb = [] }: AppLayoutProps) {
  const role = useDemoStore((s) => s.activeRole);
  const [iaOpen, setIaOpen] = useState(false);

  return (
    <div
      style={{
        fontFamily: "var(--cc-font)",
        display: "flex",
        height: "100vh",
        background: "var(--cc-gray-50)",
        overflow: "hidden",
      }}
    >
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <header
          style={{
            height: 56,
            background: "white",
            borderBottom: "1px solid var(--cc-gray-200)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            {breadcrumb.length > 0 ? (
              breadcrumb.map((crumb, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {i > 0 && <span style={{ color: "var(--cc-gray-400)", fontSize: 14 }}>/</span>}
                  <span
                    style={{
                      fontSize: 13,
                      color: i === breadcrumb.length - 1 ? "var(--cc-gray-800)" : "var(--cc-gray-500)",
                      fontWeight: i === breadcrumb.length - 1 ? 600 : 400,
                    }}
                  >
                    {crumb}
                  </span>
                </span>
              ))
            ) : (
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--cc-gray-800)" }} data-testid="text-page-title">
                {pageTitle}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ModeToggle />
            <RoleSwitcher />
            <UtilitiesMenu />
            <button
              style={{
                width: 32, height: 32, borderRadius: 8,
                border: "1px solid var(--cc-gray-200)",
                background: "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", position: "relative",
              }}
              data-testid="button-notifications"
            >
              <Bell size={15} color="var(--cc-gray-600)" />
              <span
                style={{
                  position: "absolute", top: 4, right: 4,
                  width: 8, height: 8, borderRadius: "50%",
                  background: "var(--cc-red)", border: "1.5px solid white",
                }}
              />
            </button>
            <button
              onClick={() => setIaOpen(true)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 8,
                background: "linear-gradient(135deg, var(--cc-navy) 0%, var(--cc-teal) 100%)",
                border: "none", color: "white", fontSize: 12, fontWeight: 600,
                cursor: "pointer",
              }}
              data-testid="button-ai-agent"
            >
              <Sparkles size={13} />
              Agente IA
            </button>
            <div
              style={{
                width: 32, height: 32, borderRadius: "50%",
                background: "var(--cc-navy)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "white", fontSize: 12, fontWeight: 700,
              }}
              data-testid="avatar-user"
            >
              {avatarByRole[role]}
            </div>
          </div>
        </header>
        <main style={{ flex: 1, overflow: "auto", padding: 24 }}>{children}</main>
      </div>
      <NextStepHint />
      <IaContextDrawer open={iaOpen} onClose={() => setIaOpen(false)} />
    </div>
  );
}
